import React, { useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant } from '@mprolab/uikit'
import { ethers } from 'ethers'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import type { MarketplaceCollectionRef, MarketplaceOffer } from '@mprolab/sdk'
import TypeBadge from './TypeBadge'
import ExpiryNote from './ExpiryNote'

/** Same chrome as ListingCard on purpose: one order book, two directions. */
const Card = styled.button`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    transform 0.15s ease;
  &:hover {
    border-color: ${({ theme }) => theme.colors.purple};
    transform: translateY(-1px);
  }
`

const PriceRow = styled(Flex)`
  align-items: baseline;
  justify-content: space-between;
  margin-top: 4px;
`

interface OfferCardProps {
  offer: MarketplaceOffer
  collections: MarketplaceCollectionRef[]
  displayName?: (wallet: string) => string
  onClick: () => void
}

const OfferCard: React.FC<OfferCardProps> = ({ offer, collections, onClick, displayName }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()

  const label = useMemo(
    () => collections.find((c) => c.nft === offer.nft)?.label ?? `${offer.nft.slice(0, 6)}…${offer.nft.slice(-4)}`,
    [collections, offer.nft]
  )
  const unitMpro = useMemo(() => {
    try {
      return ethers.utils.formatEther(offer.unitPriceWei)
    } catch {
      return '?'
    }
  }, [offer.unitPriceWei])
  const buyerShort = displayName?.(offer.buyer) ?? `${offer.buyer.slice(0, 6)}…${offer.buyer.slice(-4)}`

  return (
    <Card type="button" onClick={onClick}>
      <Flex justifyContent="space-between" alignItems="center">
        <TypeBadge wanted />
        <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
          {label}
        </Text>
      </Flex>
      {/* An offer wants ANY token of the collection — a quantity, never a
          specific #id, which is exactly what separates it from a listing. */}
      <Text variant={TextVariant.BODY_DEFAULT_BOLD} isTrimmed linesToDisplay={1}>
        {t('marketplace.wantedTitle', { count: Number(offer.remaining), label })}
      </Text>
      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
        {t('marketplace.offerBuyer')}: {buyerShort}
      </Text>
      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
        {t('marketplace.offeredOn', {
          date: dayjs.unix(offer.createdAt).format('YYYY-MM-DD')
        })}
      </Text>
      <ExpiryNote expiresAt={offer.expiresAt} />
      <PriceRow>
        <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
          {t('marketplace.pricePerToken')}
        </Text>
        <Text variant={TextVariant.BODY_DEFAULT_BOLD} color={colors.purple}>
          {unitMpro} MPRO
        </Text>
      </PriceRow>
    </Card>
  )
}

export default OfferCard
